/** Session-cached project access. The Edge API remains the authority. */
import { useEffect, useRef, useState } from 'react';
import { API_BASE, getAuthHeaders, supabase } from '../../../utils/supabase/client';
import type { ProjectPermissions } from './projectPermissionsModel';

export type { ProjectPermissions } from './projectPermissionsModel';

interface UseProjectPermissionsResult {
  permissions: ProjectPermissions | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  permissions: ProjectPermissions | null;
  checkedAt: number;
  /** Restored from an earlier visit: shown at once, then re-checked. */
  restored?: boolean;
  /** Read-only flags from the database policy, never saved to the device. */
  provisional?: boolean;
}

const cache = new Map<string, CacheEntry>();
const pending = new Map<string, Promise<ProjectPermissions | null>>();
const revisions = new Map<string, number>();
const listeners = new Map<string, Set<() => void>>();
const STALE_ON_RETURN_MS = 5 * 60_000;
let sessionGeneration = 0;
let realtimeUserId: string | null = null;
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;
let realtimeConnected = false;
let hasSubscribed = false;

// The permissions read goes through several sequential server lookups and
// routinely takes 2-4s. Keeping the last confirmed answer per device lets a
// project the user has opened before show its full section nav immediately;
// the server is still asked on every open and wins if it disagrees.
const STORAGE_PREFIX = 'quantis:project-permissions:v1:';
const RESTORE_MAX_AGE_MS = 14 * 24 * 60 * 60_000;
const restoredUsers = new Set<string>();

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isPermissionsShape(value: unknown): value is ProjectPermissions {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.documentFolderAccess === 'string'
    && Object.entries(record).every(([name, entry]) => (
      name === 'documentFolderAccess' || typeof entry === 'boolean'
    ));
}

function restoreUser(userId: string): void {
  if (restoredUsers.has(userId)) return;
  restoredUsers.add(userId);
  const store = storage();
  if (!store) return;
  try {
    const raw = store.getItem(STORAGE_PREFIX + userId);
    if (!raw) return;
    const saved = JSON.parse(raw) as Record<string, { permissions?: unknown; checkedAt?: unknown }>;
    const now = Date.now();
    for (const [projectId, entry] of Object.entries(saved ?? {})) {
      const key = cacheKey(projectId, userId);
      if (cache.has(key)) continue;
      if (typeof entry?.checkedAt !== 'number' || now - entry.checkedAt > RESTORE_MAX_AGE_MS) continue;
      if (!isPermissionsShape(entry.permissions)) continue;
      cache.set(key, { permissions: entry.permissions, checkedAt: entry.checkedAt, restored: true });
    }
  } catch {
    store.removeItem(STORAGE_PREFIX + userId);
  }
}

function persistUser(userId: string): void {
  const store = storage();
  if (!store) return;
  const prefix = `${userId}:`;
  const saved: Record<string, { permissions: ProjectPermissions; checkedAt: number }> = {};
  for (const [key, entry] of cache) {
    if (!key.startsWith(prefix) || !entry.permissions || entry.provisional) continue;
    saved[key.slice(prefix.length)] = { permissions: entry.permissions, checkedAt: entry.checkedAt };
  }
  try {
    store.setItem(STORAGE_PREFIX + userId, JSON.stringify(saved));
  } catch {
    // Quota or private mode: the in-memory cache still serves this session.
  }
}

function clearPersisted(): void {
  const store = storage();
  if (!store) return;
  try {
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const name = store.key(index);
      if (name?.startsWith(STORAGE_PREFIX)) store.removeItem(name);
    }
  } catch {
    // Nothing persisted is reachable, so nothing can leak to the next account.
  }
}

// Auth clears every session-bound view before another account can sign in.
// Vite also disposes the old subscription during local hot replacement.
if (typeof window !== 'undefined') {
  window.addEventListener('quantis:auth-cleared', clearProjectPermissionsCache);
}
if (import.meta.hot) import.meta.hot.dispose(() => {
  window.removeEventListener('quantis:auth-cleared', clearProjectPermissionsCache);
  clearProjectPermissionsCache();
});

function cacheKey(projectId: string, userId: string): string {
  return `${userId}:${projectId}`;
}

function evict(key: string, notify = true): void {
  const hadEntry = cache.delete(key);
  pending.delete(key);
  revisions.set(key, (revisions.get(key) ?? 0) + 1);
  if (hadEntry) persistUser(key.slice(0, key.indexOf(':')));
  if (notify) listeners.get(key)?.forEach(listener => listener());
}

function listen(key: string, listener: () => void): () => void {
  const group = listeners.get(key) ?? new Set<() => void>();
  group.add(listener);
  listeners.set(key, group);
  return () => {
    group.delete(listener);
    if (group.size === 0) listeners.delete(key);
  };
}

/** Sign-out must remove both cached access and the user-scoped subscription. */
export function clearProjectPermissionsCache(): void {
  sessionGeneration += 1;
  cache.clear();
  pending.clear();
  revisions.clear();
  restoredUsers.clear();
  clearPersisted();
  realtimeUserId = null;
  realtimeConnected = false;
  hasSubscribed = false;
  if (realtimeChannel) void supabase.removeChannel(realtimeChannel);
  realtimeChannel = null;
}

function ensureRealtime(userId: string): void {
  if (realtimeUserId === userId && realtimeChannel) return;
  if (realtimeChannel) void supabase.removeChannel(realtimeChannel);
  realtimeUserId = userId;
  realtimeConnected = false;
  hasSubscribed = false;

  // A private user topic receives changes even when a removed member can no
  // longer SELECT the changed row. The message only identifies the project;
  // the Edge API remains the authority for the current effective grant.
  const onChange = (payload: { payload?: { project_id?: unknown } }) => {
    const projectId = payload.payload?.project_id;
    if (typeof projectId === 'string') evict(cacheKey(projectId, userId));
  };
  const channel = supabase.channel(`project-access:${userId}`, { config: { private: true } })
    .on('broadcast', { event: 'changed' }, onChange)
    .subscribe(status => {
      if (realtimeUserId !== userId) return;
      if (status === 'SUBSCRIBED') {
        realtimeConnected = true;
        if (hasSubscribed) {
          for (const key of cache.keys()) {
            if (key.startsWith(`${userId}:`)) evict(key);
          }
        }
        hasSubscribed = true;
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        realtimeConnected = false;
      }
    });
  realtimeChannel = channel;
}

const VIEW_FLAGS = [
  'canViewPricing', 'canViewFinancial', 'canViewApplications', 'canViewItems',
  'canViewDocuments', 'canViewTasks', 'canViewTeam', 'canViewActivity',
  'canViewPhotos', 'canViewComments',
] as const;

/**
 * The database's own capability check (the one RLS enforces) answers for a
 * whole list in one ~300ms round trip, against 2-4s per project for the Edge
 * read. Its view flags are enough to draw the section nav straight away.
 * Every edit flag stays off until the Edge API confirms the full answer.
 */
function provisionalPermissions(flags: unknown): ProjectPermissions | null {
  if (!flags || typeof flags !== 'object' || Array.isArray(flags)) return null;
  const record = flags as Record<string, unknown>;
  const view = Object.fromEntries(VIEW_FLAGS.map((flag) => [flag, record[flag] === true])) as
    Record<(typeof VIEW_FLAGS)[number], boolean>;
  // readOnly is left unset: a member holding only view keys here may still
  // write through Edge-only authority, and the server refuses a former
  // client's writes whatever the page briefly offers.
  return {
    ...view,
    canEditPricing: false,
    canEditApplications: false,
    canEditFinancial: false,
    canEditItems: false,
    canUploadDocuments: false,
    canManageDocuments: false,
    canEditTasks: false,
    canManageTeam: false,
    canUploadPhotos: false,
    canWriteComments: false,
    documentFolderAccess: view.canViewDocuments ? 'viewer' : 'none',
  };
}

/**
 * Fill the cache for projects not yet checked this session with a
 * provisional answer, in one call for the whole list. Anything already
 * confirmed by the Edge API is left alone.
 */
export async function warmProjectPermissions(
  projectIds: readonly string[],
  userId: string | null | undefined,
): Promise<void> {
  if (!userId || projectIds.length === 0) return;
  restoreUser(userId);
  const missing = [...new Set(projectIds)]
    .filter((projectId) => !cache.has(cacheKey(projectId, userId)))
    .slice(0, 500);
  if (missing.length === 0) return;
  const generation = sessionGeneration;
  const revisionsAtStart = new Map(missing.map((projectId) => {
    const key = cacheKey(projectId, userId);
    return [key, revisions.get(key) ?? 0] as const;
  }));
  const { data, error } = await supabase.rpc('current_user_project_permissions_for_ids', {
    p_project_ids: missing,
  });
  if (error || !Array.isArray(data) || generation !== sessionGeneration) return;
  for (const row of data as Array<{ project_id?: unknown; permissions?: unknown }>) {
    if (typeof row?.project_id !== 'string') continue;
    const key = cacheKey(row.project_id, userId);
    if (cache.has(key) || revisionsAtStart.get(key) !== (revisions.get(key) ?? 0)) continue;
    const permissions = provisionalPermissions(row.permissions);
    if (!permissions) continue;
    cache.set(key, { permissions, checkedAt: Date.now(), restored: true, provisional: true });
    listeners.get(key)?.forEach((listener) => listener());
  }
}

// Several screens read one project at once, and only the first to see a
// provisional answer starts the confirming read. The rest keep drawing the
// provisional answer (every edit flag off) unless they hear this one land.
function remember(key: string, userId: string, permissions: ProjectPermissions | null): void {
  cache.set(key, { permissions, checkedAt: Date.now() });
  persistUser(userId);
  listeners.get(key)?.forEach(listener => listener());
}

async function readPermissions(
  projectId: string,
  userId: string,
  { revalidate = false }: { revalidate?: boolean } = {},
): Promise<ProjectPermissions | null> {
  const key = cacheKey(projectId, userId);
  const cached = cache.get(key);
  if (cached && !revalidate) return cached.permissions;
  const inFlight = pending.get(key);
  if (inFlight) return inFlight;

  const revision = revisions.get(key) ?? 0;
  const generation = sessionGeneration;
  const request = (async (): Promise<ProjectPermissions | null> => {
    const response = await fetch(
      `${API_BASE}/projects/${projectId}/user-permissions?userId=${userId}`,
      { headers: await getAuthHeaders() },
    );
    if (response.status === 403 || response.status === 404) {
      if (generation === sessionGeneration && revision === (revisions.get(key) ?? 0)) {
        remember(key, userId, null);
      }
      return null;
    }
    if (!response.ok) throw new Error(`Could not load project access (${response.status})`);
    const result = await response.json() as {
      success: boolean;
      permissions?: ProjectPermissions;
      accessMode?: string;
      error?: string;
    };
    if (!result.success || !result.permissions) {
      throw new Error(result.error || 'Could not load project access');
    }
    const permissions: ProjectPermissions = {
      ...result.permissions,
      readOnly: result.accessMode === 'read_only',
    };
    if (generation === sessionGeneration && revision === (revisions.get(key) ?? 0)) {
      remember(key, userId, permissions);
    }
    return permissions;
  })();
  pending.set(key, request);
  try {
    return await request;
  } finally {
    if (pending.get(key) === request) pending.delete(key);
  }
}

/**
 * Start the permissions read as soon as the user shows intent to open a
 * project (hover, click), so it overlaps the route change and lazy chunk load
 * instead of starting only once ProjectDetail has mounted.
 */
export function prefetchProjectPermissions(projectId: string | null | undefined, userId: string | null | undefined): void {
  if (!projectId || !userId) return;
  restoreUser(userId);
  const key = cacheKey(projectId, userId);
  if (cache.has(key) || pending.has(key)) return;
  void readPermissions(projectId, userId).catch(() => undefined);
  void warmProjectPermissions([projectId], userId).catch(() => undefined);
}

export function useProjectPermissions(
  projectId: string | null,
  userId: string | null,
): UseProjectPermissionsResult {
  if (userId) restoreUser(userId);
  const key = projectId && userId ? cacheKey(projectId, userId) : null;
  const initial = key ? cache.get(key) : null;
  const [permissions, setPermissions] = useState<ProjectPermissions | null>(initial?.permissions ?? null);
  const [loadedFor, setLoadedFor] = useState<string | null>(initial ? key : null);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);

  const fetchPermissions = async (force = false): Promise<void> => {
    const generation = ++requestGeneration.current;
    if (!projectId || !userId || !key) {
      setPermissions(null);
      setLoadedFor(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (force) evict(key, false);
    const cached = cache.get(key);
    if (cached) {
      setPermissions(cached.permissions);
      setLoadedFor(cached.permissions ? key : null);
      setLoading(false);
      if (cached.restored) {
        // Confirm the saved answer in the background; the nav stays up.
        cached.restored = false;
        try {
          const latest = await readPermissions(projectId, userId, { revalidate: true });
          if (generation !== requestGeneration.current) return;
          setPermissions(latest);
          setLoadedFor(latest ? key : null);
        } catch {
          // Keep the saved answer; the server still gates every protected read.
        }
      }
      return;
    }

    // Do not unmount the project during a background check. Protected reads
    // and writes are still gated on the server, not by this display cache.
    if (loadedFor !== key) setLoading(true);
    // A direct link skips the list, so ask the fast source here too. If it
    // lands first, the listener below redraws with its provisional answer.
    void warmProjectPermissions([projectId], userId).catch(() => undefined);
    setError(null);
    try {
      const latest = await readPermissions(projectId, userId);
      if (generation !== requestGeneration.current) return;
      setPermissions(latest);
      setLoadedFor(latest ? key : null);
    } catch (cause) {
      if (generation !== requestGeneration.current) return;
      setError(cause instanceof Error ? cause.message : 'Could not load project access');
      if (loadedFor !== key) setPermissions(null);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId || !userId || !key) {
      void fetchPermissions();
      return;
    }
    ensureRealtime(userId);
    void fetchPermissions();
    const unsubscribe = listen(key, () => { void fetchPermissions(); });
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const checkedAt = cache.get(key)?.checkedAt ?? 0;
      const maxAge = realtimeConnected ? STALE_ON_RETURN_MS : 60_000;
      if (Date.now() - checkedAt > maxAge) {
        void fetchPermissions(true);
      }
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', refreshOnReturn);
      requestGeneration.current += 1;
    };
    // Identity changes resubscribe. A render must not launch another read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, userId]);

  return {
    permissions: loadedFor === key ? permissions : null,
    loading,
    error,
    refetch: () => fetchPermissions(true),
  };
}
